import React, { useState } from 'react';
import { LayoutDashboard, LogOut, Home, ChevronLeft, ChevronRight } from 'lucide-react';
import { ActiveTab } from '../../../models';

export interface MenuItem {
  id: ActiveTab;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

interface SidebarProps {
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  menuItems: MenuItem[];
  title?: string;
  branchName?: string;
  adminName?: string;
  avatarUrl?: string;
  onLogout?: () => void;
  onGoHome?: () => void;
  roleLabel?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  menuItems,
  title = "Dashboard",
  branchName,
  adminName,
  avatarUrl,
  onLogout,
  onGoHome,
  roleLabel,
  isCollapsed: controlledIsCollapsed,
  onToggleCollapse: controlledOnToggleCollapse,
}) => {
  const [localIsCollapsed, setLocalIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  const isCollapsed = controlledIsCollapsed !== undefined ? controlledIsCollapsed : localIsCollapsed;

  const toggleCollapse = () => {
    if (controlledOnToggleCollapse) {
      controlledOnToggleCollapse();
    } else {
      const newState = !localIsCollapsed;
      setLocalIsCollapsed(newState);
      localStorage.setItem('sidebar-collapsed', String(newState));
    }
  };

  return (
    <nav className={`bg-slate-900 text-white h-full flex flex-col flex-shrink-0 animate-in fade-in slide-in-from-left duration-500 transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`}>
      {/* Brand & Collapse Header */}
      <div className={`p-4 flex items-center border-b border-slate-800/40 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <span className="font-black text-xs text-indigo-400 tracking-widest pl-2 uppercase truncate max-w-[170px]">
            {title}
          </span>
        )}
        <button
          onClick={toggleCollapse}
          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
          title={isCollapsed ? "메뉴 펼치기" : "메뉴 접기"}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Brand Header */}
      <div className="p-4 pb-2">
        {/* Home Button */}
        {isCollapsed ? (
          <button
            onClick={() => onGoHome ? onGoHome() : window.location.href = '/'}
            className="w-full flex items-center justify-center p-3 mb-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-900/20 transition-all duration-200 group border border-indigo-500/50"
            title="홈 화면으로 이동"
          >
            <Home size={18} className="group-hover:scale-110 transition-transform" />
          </button>
        ) : (
          <button
            onClick={() => onGoHome ? onGoHome() : window.location.href = '/'}
            className="w-full flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-900/20 transition-all duration-200 group border border-indigo-500/50"
          >
            <div className="bg-white/20 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
              <Home size={16} className="text-white" />
            </div>
            <span className="font-black text-xs uppercase tracking-widest truncate">홈 화면으로 이동</span>
          </button>
        )}

        {/* Profile Section */}
        {isCollapsed ? (
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl overflow-hidden ring-2 ring-indigo-500/20 shadow-xl bg-slate-700" title={`${adminName} (${roleLabel || 'Admin'})`}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt={adminName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-indigo-600 text-white font-black text-base">
                    {adminName?.substring(0, 1)}
                  </div>
                )}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900 animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="bg-slate-800/40 rounded-2xl p-4 border border-slate-700/50 backdrop-blur-sm mb-6">
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-xl overflow-hidden ring-2 ring-indigo-500/20 shadow-xl bg-slate-700">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={adminName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-indigo-600 text-white font-black text-base">
                      {adminName?.substring(0, 1)}
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900 animate-pulse" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="font-black text-sm text-white truncate leading-tight mb-0.5">{adminName}</h4>
                {branchName ? (
                  <div className="flex flex-col">
                    <span className="font-bold text-[11px] text-indigo-400 truncate">{branchName}</span>
                    <span className="text-[9px] font-medium text-slate-500 uppercase tracking-tighter">{roleLabel || "Branch Manager"}</span>
                  </div>
                ) : (
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">{roleLabel || "Administrator Account"}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div className="flex-1 px-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;

          if (isCollapsed) {
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as ActiveTab)}
                className={`w-full flex items-center justify-center p-3 rounded-xl transition-all duration-200 relative ${isActive
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                title={item.label}
              >
                <Icon size={18} />
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 text-[9px] font-bold px-1 py-0.5 rounded-full min-w-[16px] text-center bg-rose-500 text-white z-10">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as ActiveTab)}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-200 ${isActive
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
            >
              <div className="flex items-center space-x-3 truncate">
                <Icon size={18} className="shrink-0" />
                <span className="font-bold text-sm tracking-tight truncate">{item.label}</span>
              </div>
              {item.badge !== undefined && item.badge > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shrink-0 ${isActive ? 'bg-white text-indigo-600' : 'bg-rose-500 text-white'
                  }`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bottom Section: Logout */}
      <div className="p-4 border-t border-slate-800/50">
        {isCollapsed ? (
          <button
            onClick={() => onLogout ? onLogout() : window.location.href = '/'}
            className="w-full flex items-center justify-center p-3 rounded-xl text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all duration-200 group"
            title="로그아웃"
          >
            <LogOut size={18} className="group-hover:scale-110 transition-transform" />
          </button>
        ) : (
          <button
            onClick={() => onLogout ? onLogout() : window.location.href = '/'}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all duration-200 group"
          >
            <LogOut size={18} className="group-hover:scale-110 transition-transform shrink-0" />
            <span className="font-bold text-[10px] uppercase tracking-widest truncate">로그아웃</span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default Sidebar;
